import { useEffect, useState } from 'react';

import classNames from 'classnames';
import { computed } from 'mobx';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import Button from '../../../../components/buttons/Button';
import { DialogLoader } from '../../../../components/Dialog/DialogLoader';
import { DialogHeader } from '../../../../components/Dialog/DialogUiRedesign/DialogHeader';
import { ErrorMessage } from '../../../../components/ErrorMessage/ErrorMessage';
import type { WizardChildProps } from '../../../../components/Wizard/types';
import { useMediaQuery } from '../../../../hooks/useMediaQuery';
import { analytics } from '../../../../services/analytics';
import { useStores } from '../../../../stores/useStores';
import { emailValidator } from '../../../../utils/validators';
import { useHandleAuth } from '../../useHandleAuth';
import { useOpenAuthWizard } from '../../useOpenAuthWizard';
import { HeaderLogoElement } from '../HeaderLeftElement/HeaderLogoElement';
import { RedesignContainer } from '../RedesignContainer/RedesignContainer';
import { RedesignEmailAndPasswordInputs } from '../RedesignEmailAndPasswordInputs/RedesignEmailAndPasswordInputs';
import { ThirdPartyAuthRedesign } from '../ThirdPartyAuthRedesign/ThirdPartyAuthRedesign';

import styles from './LogInOptionsRedesign.module.scss';

export const LogInOptionsRedesign = observer(function LogInOptionsRedesign(props: WizardChildProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [fieldsErrors, setFieldsErrors] = useState({ password: '', email: '' });

  const { t } = useTranslation();

  const { authStore, portalStore } = useStores();
  const { isSigningUp } = authStore;

  const { openAuthWizard } = useOpenAuthWizard();

  const landingKey = authStore.getLandingKey();

  const isDesktop = useMediaQuery('(min-width: 768px)');

  const { handleAuth } = useHandleAuth(setSubmitError, props.nextStep);

  const clearError = () => setFieldsErrors({ password: '', email: '' });

  const isProcessing = computed(() => authStore.pending('signIn')).get();

  const openResetPassword = () => {
    openAuthWizard('resetPasswordWizard', {
      force: true,
      landingKey: landingKey ?? undefined,
    });

    void analytics.sendEvent('reset_password_button', {
      event_action: 'click',
      event_category: 'sign_up',
    });
  };

  const handleSubmit = () => {
    const emailError = emailValidator(email.trim());

    if (emailError) {
      setFieldsErrors((prev) => ({ ...prev, email: emailError }));
      return;
    }

    if (!password.trim()) {
      setFieldsErrors((prev) => ({ ...prev, password: t('auth.pleaseFillInAllRequiredFields') }));
      return;
    }

    void analytics.sendEvent('login_account', {
      event_action: 'click',
      event_category: 'sign_up',
      event_type: 'login_platform',
      event_value: 'email',
      button_text: 'log in',
    });

    void handleAuth({
      provider: 'email',
      action: 'log_in',
      credentials: {
        email,
        password,
      },
    });
  };

  const onGoBack = () => {
    portalStore.closePortal();
  };

  const goToSignUp = () => {
    void analytics.sendEvent('signup_account_button', {
      event_category: 'sign_up',
      event_action: 'click',
      button_text: 'sign up',
    });
    openAuthWizard('signUpWizard', {
      triggeredBy: 'log_in',
      force: true,
      landingKey: landingKey ?? undefined,
    });
  };

  useEffect(() => {
    if (props.isCurrent) {
      void analytics.sendEvent('login_page', {
        event_action: 'impression',
        event_category: 'sign_up',
      });
    }
  }, [props.isCurrent]);

  return (
    <RedesignContainer className={styles.container}>
      <DialogHeader
        className={styles.dialogHeader}
        closeBtnDataTestId="close-login"
        decorationsWrapperClassName={styles.dialogHeaderDecorationsWrapperClassName}
        goBackBtnDataTestId="signin-back-button"
        headerLeftElement={<HeaderLogoElement position="left" showLogo={isDesktop} />}
        headerRightElement={<HeaderLogoElement position="right" showLogo={!isDesktop} />}
        title={t('auth.logIn')}
        {...(isDesktop ? { onClose: onGoBack } : { onGoBack })}
      />
      <DialogLoader isProcessing={isProcessing || isSigningUp} />
      <div className={styles.containerContent}>
        <div className={styles.thirdPartyAuthWrapper}>
          <div className={styles.signUpOptionsSubtitleWrapper}>
            <span className={styles.signUpOptionsSubtitle}>{t('auth.logInWith')}</span>
          </div>

          <ThirdPartyAuthRedesign action="Log in" />
        </div>

        <div className={styles.rowLineContainer}>
          <div className={classNames(styles.lineGradient, styles.lineGradientLeft)} />
          <span className={styles.orEmailText}>{t('auth.orEmail')}</span>
          <div className={classNames(styles.lineGradient, styles.lineGradientRight)} />
        </div>

        <RedesignEmailAndPasswordInputs
          className={styles.emailAndPasswordInputsWrapper}
          disabled={isSigningUp}
          emailError={fieldsErrors.email}
          hideErrors={true}
          passwordError={fieldsErrors.password}
          type="log_in"
          onChangeEmail={(newEmail) => {
            clearError();
            setEmail(newEmail);
          }}
          onChangePassword={(newPassword) => {
            clearError();
            setPassword(newPassword);
          }}
        />

        <div
          className={classNames(styles.forgotPasswordLink, {
            'animation-slide-down': true,
            'form-links': true,
          })}
        >
          <span>{t('auth.forgotPasswordQuestion')}</span>
          <span className={styles.loginLink} data-testid="reset-password-btn" onClick={openResetPassword}>
            {t('auth.clickHere')}
          </span>
        </div>

        <ErrorMessage className={styles.authErrorMessage}>
          {submitError || fieldsErrors.email || fieldsErrors.password}
        </ErrorMessage>

        <div className={styles.createAccountEmailButtonWrapper}>
          <Button btnType="main" className={styles.emailSignUpButton} dataTestId="log-in-btn" onClick={handleSubmit}>
            {t('auth.logIn')}
          </Button>
        </div>

        <div
          className={classNames({
            'animation-slide-down': true,
            'form-links': true,
          })}
        >
          <span>{t('auth.doNotHaveAccountYet')}</span>
          <span className={styles.loginLink} data-testid="lobby-signup-btn" onClick={goToSignUp}>
            {t('auth.signUpV2')}
          </span>
        </div>
      </div>
    </RedesignContainer>
  );
});
